import {
  type ReactElement,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '~/components/ui/BrandLogo';
import { homeMedia } from './homeMedia';

const navItems = [
  { href: '#overview' },
  { href: '#showcase' },
  { href: '#materials' },
  { href: '#timeline' },
];

type VideoClip = { src: string; poster: string; label: string; tag: string };
type WorkStory = { clip: VideoClip; brief: string; result: string };

const LazyHomeVideo = ({
  src,
  poster,
  className,
  label,
  decorative = false,
  load = 'visible',
}: {
  src: string;
  poster: string;
  className: string;
  label?: string;
  decorative?: boolean;
  load?: 'eager' | 'visible' | 'interaction';
}): ReactElement => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [enabled, setEnabled] = useState(
    () =>
      load === 'eager' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video || load !== 'visible' || enabled) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (
          entries.some(entry => entry.isIntersecting) &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          setEnabled(true);
          observer.disconnect();
        }
      },
      { rootMargin: '240px' }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [enabled, load]);

  useEffect(() => {
    if (enabled) void videoRef.current?.play().catch(() => undefined);
  }, [enabled]);

  const enableForInteraction = (): void => {
    if (
      load === 'interaction' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setEnabled(true);
    }
  };

  return (
    <video
      {...(decorative ? { 'aria-hidden': true } : { 'aria-label': label })}
      autoPlay={enabled}
      className={className}
      loop
      muted
      onFocus={enableForInteraction}
      onPointerEnter={enableForInteraction}
      playsInline
      poster={poster}
      preload="none"
      ref={videoRef}
      src={enabled ? src : undefined}
    />
  );
};

const useHomeScrollEffects = (
  rootRef: RefObject<HTMLDivElement | null>
): void => {
  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    void Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger'),
      import('gsap/Flip'),
    ]).then(([gsapModule, scrollTriggerModule, flipModule]) => {
      if (cancelled || !rootRef.current) {
        return;
      }

      const gsap = gsapModule.gsap;
      const ScrollTrigger = scrollTriggerModule.ScrollTrigger;
      const Flip = flipModule.Flip;

      gsap.registerPlugin(ScrollTrigger, Flip);

      const context = gsap.context(() => {
        let revealObserver: IntersectionObserver | undefined;

        if (!prefersReducedMotion) {
          const heroTitleWords =
            gsap.utils.toArray<HTMLElement>('.hero-title-word');

          if (heroTitleWords.length > 0) {
            gsap
              .timeline({ defaults: { ease: 'power3.out' } })
              .fromTo(
                '.hero-title-rule',
                { autoAlpha: 0, scaleX: 0 },
                { autoAlpha: 1, duration: 0.72, scaleX: 1 }
              )
              .fromTo(
                heroTitleWords,
                { autoAlpha: 0, rotateX: 38, yPercent: 110 },
                {
                  autoAlpha: 1,
                  duration: 0.9,
                  rotateX: 0,
                  stagger: 0.08,
                  yPercent: 0,
                },
                0.08
              )
              .fromTo(
                '.hero-copy',
                { autoAlpha: 0, y: 22 },
                { autoAlpha: 1, duration: 0.7, y: 0 },
                0.46
              )
              .fromTo(
                '.hero-card-motion',
                { autoAlpha: 0, filter: 'blur(10px)', scale: 0.94, y: 36 },
                {
                  autoAlpha: 1,
                  duration: 0.92,
                  filter: 'blur(0px)',
                  scale: 1,
                  stagger: 0.1,
                  y: 0,
                },
                0.22
              )
              .fromTo(
                '.hero-ui-motion',
                { autoAlpha: 0, scale: 0.96, y: 18 },
                {
                  autoAlpha: 1,
                  duration: 0.62,
                  scale: 1,
                  stagger: 0.08,
                  y: 0,
                },
                0.68
              );

            gsap.to('.hero-title', {
              ease: 'none',
              scrollTrigger: {
                end: 'bottom top',
                scrub: 1,
                start: 'top top',
                trigger: '.home-hero',
              },
              yPercent: -8,
            });
          }
          const animateSection = (
            sectionId: string,
            itemSelector: string
          ): void => {
            const section = root.querySelector<HTMLElement>(sectionId);

            if (!section) {
              return;
            }

            const copy = section.querySelectorAll<HTMLElement>(
              '.section-copy-motion'
            );
            const items = section.querySelectorAll<HTMLElement>(itemSelector);

            if (copy.length > 0) {
              gsap.fromTo(
                copy,
                { autoAlpha: 0, y: 26 },
                {
                  autoAlpha: 1,
                  duration: 0.72,
                  ease: 'power3.out',
                  stagger: 0.08,
                  scrollTrigger: {
                    start: 'top 76%',
                    toggleActions: 'play none none reverse',
                    trigger: section,
                  },
                  y: 0,
                }
              );
            }

            if (items.length > 0) {
              gsap.fromTo(
                items,
                { autoAlpha: 0, scale: 0.96, y: 34 },
                {
                  autoAlpha: 1,
                  duration: 0.78,
                  ease: 'power3.out',
                  scale: 1,
                  stagger: 0.08,
                  scrollTrigger: {
                    start: 'top 66%',
                    toggleActions: 'play none none reverse',
                    trigger: section,
                  },
                  y: 0,
                }
              );
            }
          };

          const animateMaterials = (): void => {
            const section = root.querySelector<HTMLElement>('#materials');

            if (!section) {
              return;
            }

            section.classList.add('scroll-observed-section');
            section
              .querySelectorAll<HTMLElement>(
                '.section-copy-motion, .material-flow-motion, .material-step-motion, .material-submit-motion'
              )
              .forEach((item, index) => {
                item.classList.add('scroll-reveal-motion');
                item.style.setProperty(
                  '--scroll-reveal-delay',
                  `${index * 70}ms`
                );
              });

            const line = section.querySelector<HTMLElement>(
              '.material-line-motion'
            );

            if (line) {
              line.classList.add('scroll-line-motion');
            }
          };

          const animateTimeline = (): void => {
            const section = root.querySelector<HTMLElement>('#timeline');

            if (!section) {
              return;
            }

            section.classList.add('scroll-observed-section');
            section
              .querySelectorAll<HTMLElement>(
                '.section-copy-motion, .timeline-step-motion'
              )
              .forEach((item, index) => {
                item.classList.add('scroll-reveal-motion');
                item.style.setProperty(
                  '--scroll-reveal-delay',
                  `${index * 90}ms`
                );
              });
            section
              .querySelectorAll<HTMLElement>('.timeline-line-motion')
              .forEach(line => line.classList.add('scroll-line-motion'));
          };

          const animateContext = (): void => {
            const section = root.querySelector<HTMLElement>('#context');

            if (!section) {
              return;
            }

            section.classList.add('scroll-observed-section');
            section
              .querySelectorAll<HTMLElement>(
                '.context-copy-motion, .context-card-motion'
              )
              .forEach((item, index) => {
                item.classList.add('scroll-reveal-motion');
                item.style.setProperty(
                  '--scroll-reveal-delay',
                  `${index * 85}ms`
                );
              });
          };

          animateSection('#overview', '.overview-chip-motion');
          animateMaterials();
          animateContext();
          animateTimeline();

          const observer = new IntersectionObserver(
            entries => {
              entries.forEach(entry => {
                entry.target.classList.toggle(
                  'is-visible',
                  entry.isIntersecting
                );
              });
            },
            { rootMargin: '0px 0px -16% 0px', threshold: 0.18 }
          );
          revealObserver = observer;

          root
            .querySelectorAll<HTMLElement>(
              '#materials .scroll-reveal-motion, #materials .scroll-line-motion, #context .scroll-reveal-motion, #timeline .scroll-reveal-motion, #timeline .scroll-line-motion'
            )
            .forEach(item => observer.observe(item));

          gsap.utils.toArray<HTMLElement>('.home-section').forEach(section => {
            if (
              section.id === 'overview' ||
              section.id === 'showcase' ||
              section.id === 'materials' ||
              section.id === 'context' ||
              section.id === 'timeline'
            ) {
              return;
            }

            gsap.fromTo(
              section,
              { opacity: 0.76, y: 56, scale: 0.985 },
              {
                ease: 'none',
                opacity: 1,
                scale: 1,
                scrollTrigger: {
                  end: 'top 48%',
                  scrub: 0.7,
                  start: 'top 88%',
                  trigger: section,
                },
                y: 0,
              }
            );
          });

          gsap.utils
            .toArray<HTMLElement>('.hero-layer')
            .forEach((layer, index) => {
              gsap.to(layer, {
                ease: 'none',
                rotate: index % 2 === 0 ? -1.5 : 1.5,
                scrollTrigger: {
                  end: 'bottom top',
                  scrub: 1.1,
                  start: 'top top',
                  trigger: '.home-hero',
                },
                yPercent: index % 2 === 0 ? -14 : 10,
              });
            });
        }

        const gallery = root.querySelector<HTMLElement>('#showcase-gallery');
        const galleryItems =
          gallery?.querySelectorAll<HTMLElement>('.gallery__item');
        let flipContext: { revert: () => void } | undefined;
        let resizeTimer = 0;

        if (prefersReducedMotion) {
          gallery?.classList.add('gallery--final');
        }

        const destroyBentoTween = (): void => {
          ScrollTrigger.getById('showcase-bento')?.kill(true);
          flipContext?.revert();
          flipContext = undefined;

          if (!gallery || !galleryItems) {
            return;
          }

          gsap.killTweensOf(galleryItems);
          gsap.killTweensOf('.bento-heading');
          gsap.set(galleryItems, { clearProps: 'all' });
          gsap.set('.bento-heading', { clearProps: 'transform' });
          gallery.classList.remove('gallery--final');
        };

        const createBentoTween = (): void => {
          if (!gallery || !galleryItems || galleryItems.length === 0) {
            return;
          }

          destroyBentoTween();

          flipContext = gsap.context(() => {
            gallery.classList.add('gallery--final');
            const flipState = Flip.getState(galleryItems);
            gallery.classList.remove('gallery--final');

            const flip = Flip.to(flipState, {
              absolute: true,
              ease: 'none',
              scale: false,
              stagger: 0.015,
            });

            const timeline = gsap.timeline({
              scrollTrigger: {
                anticipatePin: 1,
                end: '+=4000',
                id: 'showcase-bento',
                invalidateOnRefresh: true,
                pin: true,
                scrub: 0.85,
                start: 'top top+=64',
                trigger: gallery.parentElement,
              },
            });

            timeline.add(flip).to('.bento-heading', { y: -28 }, 0);
          }, root);
        };

        const handleBentoResize = (): void => {
          window.clearTimeout(resizeTimer);
          destroyBentoTween();

          resizeTimer = window.setTimeout(() => {
            createBentoTween();
            ScrollTrigger.refresh();
          }, 240);
        };

        if (
          !prefersReducedMotion &&
          gallery &&
          galleryItems &&
          galleryItems.length > 0
        ) {
          createBentoTween();
          window.addEventListener('resize', handleBentoResize);

          cleanup = () => {
            window.removeEventListener('resize', handleBentoResize);
            window.clearTimeout(resizeTimer);
            revealObserver?.disconnect();
            destroyBentoTween();
            context.revert();
          };
        }

        if (!prefersReducedMotion) {
          gsap.to('.footer-video', {
            ease: 'none',
            scale: 1.12,
            scrollTrigger: {
              end: 'bottom top',
              scrub: 1,
              start: 'top bottom',
              trigger: '.footer-cta',
            },
            yPercent: -8,
          });
        }

        ScrollTrigger.refresh();
      }, root);

      cleanup ??= () => context.revert();
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [rootRef]);
};

const HeroButton = ({
  children,
  href = '/agent',
  variant = 'solid',
}: {
  children: string;
  href?: string;
  variant?: 'solid' | 'ghost';
}): ReactElement => {
  const className =
    variant === 'solid'
      ? 'inline-flex min-h-10 items-center justify-center rounded-full border border-[#7c3aed]/30 bg-[#8b5cf6] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_36px_rgba(139,92,246,0.22)] transition hover:bg-[#7c3aed] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]'
      : 'inline-flex min-h-12 items-center justify-center rounded-full border border-[#a78bfa]/35 bg-white/70 px-6 py-3 text-sm font-semibold text-[#31204f] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)] transition hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]';

  if (href.startsWith('#')) {
    return (
      <a className={className} href={href}>
        {children}
      </a>
    );
  }

  return (
    <Link className={className} to={href}>
      {children}
    </Link>
  );
};

const VideoFrame = ({
  clip,
  className = '',
  priority = false,
}: {
  clip: VideoClip;
  className?: string;
  priority?: boolean;
}): ReactElement => (
  <figure
    className={`group relative overflow-hidden rounded-lg border border-white/70 bg-white shadow-[0_22px_60px_rgba(0,80,115,0.16)] ${className}`}
  >
    <span
      aria-hidden="true"
      className="absolute inset-0 bg-[linear-gradient(135deg,#f0eaff_0%,#dff7ff_46%,#fff8e8_100%)]"
    />
    <LazyHomeVideo
      className="absolute inset-0 size-full object-cover motion-reduce:hidden"
      label={clip.label}
      load={priority ? 'eager' : 'interaction'}
      poster={clip.poster}
      src={clip.src}
    />
    <span
      aria-hidden="true"
      className="absolute inset-0 bg-[linear-gradient(180deg,rgba(4,12,24,0.02)_0%,rgba(4,12,24,0.68)_100%)]"
    />
    <figcaption className="absolute inset-x-4 bottom-4">
      <span className="inline-flex border border-white/40 bg-white/20 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur">
        {clip.tag}
      </span>
      <p className="mt-2 text-sm font-semibold leading-5 text-white drop-shadow">
        {clip.label}
      </p>
    </figcaption>
  </figure>
);

const FilmRail = ({
  stories,
  label,
}: {
  stories: WorkStory[];
  label: string;
}): ReactElement => (
  <div className="bento-pin relative z-20 overflow-visible">
    <div
      aria-hidden="true"
      className="absolute inset-[6%] rounded-lg bg-[radial-gradient(circle_at_26%_22%,rgba(0,166,214,0.2),transparent_30%),radial-gradient(circle_at_72%_70%,rgba(139,92,246,0.16),transparent_34%),linear-gradient(135deg,rgba(255,255,255,0.72),rgba(255,255,255,0.12))] blur-md"
    />
    <div
      aria-label={label}
      className="gallery gallery--bento focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7ddcff]"
      id="showcase-gallery"
      role="list"
      tabIndex={0}
    >
      {stories.map((story, index) => (
        <article
          className="gallery__item group border border-white/80 bg-white shadow-[0_30px_90px_rgba(0,80,115,0.16)] transition duration-300 hover:border-[#7ddcff] motion-reduce:transition-none"
          key={`${story.clip.src}-${index}`}
          role="listitem"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 z-0 bg-[linear-gradient(135deg,#f0eaff_0%,#dff7ff_46%,#fff8e8_100%)]"
          />
          <LazyHomeVideo
            className="gallery__item-img absolute inset-0 z-10 size-full object-cover motion-reduce:hidden"
            label={story.clip.label}
            poster={story.clip.poster}
            src={story.clip.src}
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 z-20 bg-[linear-gradient(180deg,rgba(6,12,24,0)_0%,rgba(6,12,24,0.1)_48%,rgba(6,12,24,0.78)_100%)]"
          />
          <div className="absolute left-4 top-4 z-30 flex items-center gap-2">
            <span className="grid size-10 place-items-center rounded-full border border-white/60 bg-white/28 font-mono text-[11px] font-semibold text-white backdrop-blur">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="hidden border border-white/50 bg-white/24 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur sm:inline-flex">
              {story.clip.tag}
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 z-30 p-4 sm:p-5">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-white/86">
              {story.brief}
            </p>
            <h3 className="mt-2 text-xl font-semibold leading-tight text-white sm:text-2xl">
              {story.clip.label}
            </h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-white/88">
              {story.result}
            </p>
          </div>
        </article>
      ))}
    </div>
  </div>
);

export const HomePage = (): ReactElement => {
  const { t } = useTranslation('home');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const strings = (key: string): string[] =>
    t(key, { returnObjects: true }) as string[];
  const matrix = (key: string): string[][] =>
    t(key, { returnObjects: true }) as string[][];
  const formats = strings('formats');
  const submitItems = strings('submitItems');
  const timeline = strings('timeline');
  const pipelineSteps = matrix('pipeline').map(([label, value]) => ({
    label,
    value,
  }));
  const platformGroups = matrix('groups').map(([title, ...items]) => ({
    title,
    items,
  }));
  const clips = matrix('clips');
  const videoClips: VideoClip[] = homeMedia.map((media, index) => ({
    ...media,
    label: clips[index]?.[0] ?? '',
    tag: clips[index]?.[1] ?? '',
  }));
  const showcaseStories: WorkStory[] = matrix('stories').map(
    ([brief, result], index) => ({ clip: videoClips[index], brief, result })
  );
  const navLabels = strings('nav');
  const actions = strings('actions');
  const heroLines = strings('hero.titleLines');
  const heroHighlights = strings('hero.highlights');
  const heroPrompt = strings('hero.prompt');
  const overview = strings('overview');
  const showcase = strings('showcase');
  const materials = strings('materials');
  const context = strings('context');
  const footer = strings('footer');

  useHomeScrollEffects(rootRef);

  useEffect(() => {
    let frame = 0;

    const syncHeader = (): void => {
      frame = 0;
      setHeaderScrolled(window.scrollY > 16);
    };

    const handleScroll = (): void => {
      if (frame !== 0) {
        return;
      }

      frame = window.requestAnimationFrame(syncHeader);
    };

    syncHeader();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }

      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#f7f9ff] text-[#141827]"
      ref={rootRef}
    >
      <a
        className="fixed left-3 top-3 z-[60] -translate-y-20 rounded-lg bg-[#141827] px-3 py-2 text-sm font-semibold text-white focus:translate-y-0"
        href="#home-main"
      >
        {t('skip')}
      </a>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition duration-300 ${
          headerScrolled
            ? 'bg-[#f9fbff]/68 shadow-[0_18px_48px_rgba(49,32,79,0.08)] backdrop-blur-xl'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex min-h-14 w-full max-w-[1240px] items-center justify-between gap-4 px-4 py-2 sm:px-6 lg:px-10">
          <Link
            aria-label={t('homeLabel')}
            className="rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
            to="/"
          >
            <BrandLogo className="h-auto w-[118px] sm:w-[144px]" />
          </Link>

          <nav
            aria-label={t('sectionsLabel')}
            className="hidden items-center gap-1 md:flex"
          >
            {navItems.map((item, index) => (
              <a
                className="rounded-lg px-3 py-2 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-[#746987] transition hover:bg-[#ede9ff] hover:text-[#24153f] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
                href={item.href}
                key={item.href}
              >
                {navLabels[index]}
              </a>
            ))}
          </nav>

          <HeroButton>{actions[0]}</HeroButton>
        </div>
        <div className="mx-auto flex w-full max-w-[1240px] items-center gap-1 overflow-x-auto px-4 pb-2 sm:px-6 md:hidden">
          {navItems.map((item, index) => (
            <a
              className="shrink-0 rounded-lg px-3 py-2 font-mono text-xs font-semibold uppercase tracking-[0.1em] text-[#746987] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
              href={item.href}
              key={item.href}
            >
              {navLabels[index]}
            </a>
          ))}
        </div>
      </header>
      <main id="home-main">
        <section
          aria-labelledby="home-hero-title"
          className="home-hero relative isolate overflow-hidden border-b border-[#d8ccff] px-4 pb-8 pt-32 sm:px-6 md:pt-24 lg:px-10"
        >
          <LazyHomeVideo
            className="hero-bg-video absolute inset-0 -z-30 size-full object-cover opacity-[0.68] brightness-[1.08] contrast-[1.08] saturate-[1.22] motion-reduce:hidden"
            decorative
            load="eager"
            poster={videoClips[8].poster}
            src={videoClips[8].src}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-20 bg-[linear-gradient(90deg,rgba(249,251,255,0.94)_0%,rgba(249,251,255,0.82)_34%,rgba(249,251,255,0.34)_66%,rgba(249,251,255,0.62)_100%),radial-gradient(circle_at_78%_34%,rgba(0,166,214,0.12),transparent_34%),radial-gradient(circle_at_58%_18%,rgba(139,92,246,0.12),transparent_28%)]"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-[repeating-linear-gradient(180deg,rgba(72,52,115,0.035)_0_1px,transparent_1px_7px),linear-gradient(90deg,rgba(139,92,246,0.08),transparent_34%,rgba(0,166,214,0.1)_78%,transparent)] opacity-65 mix-blend-multiply"
          />

          <div className="mx-auto grid min-h-[calc(100svh-7rem-80px)] w-full max-w-[1240px] items-center gap-10 py-8 md:py-10 lg:grid-cols-[0.86fr_1.14fr] lg:py-12">
            <div className="max-w-[720px]">
              <div className="flex flex-wrap items-center gap-3">
                <span className="border border-[#8b5cf6]/35 bg-white/70 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6d28d9] shadow-[inset_0_1px_0_rgba(255,255,255,0.78)]">
                  Mylo Studio
                </span>
                <span className="border border-[#00a6d6]/30 bg-[#e8f9ff]/80 px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-[#006b8f]">
                  {t('hero.badge')}
                </span>
              </div>
              <div
                aria-hidden="true"
                className="hero-title-rule mt-5 h-0.5 w-full max-w-[360px] origin-left bg-[linear-gradient(90deg,#8b5cf6_0%,#00a6d6_54%,#d946ef_100%)] shadow-[0_0_18px_rgba(0,166,214,0.22)]"
              />
              <h1
                aria-label={t('hero.title')}
                className="hero-title mt-8 max-w-2xl text-balance text-[clamp(3.35rem,6vw,6.35rem)] font-semibold leading-[1.02] text-[#141827] [perspective:900px] [text-shadow:0_1px_0_rgba(255,255,255,0.72)]"
                id="home-hero-title"
              >
                <span
                  aria-hidden="true"
                  className="block overflow-hidden pb-[0.16em]"
                >
                  <span className="hero-title-word inline-block will-change-transform">
                    {heroLines[0]}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className="block overflow-hidden pb-[0.16em]"
                >
                  <span className="hero-title-word inline-block will-change-transform">
                    {heroLines[1]}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className="block overflow-hidden pb-[0.16em]"
                >
                  <span className="hero-title-word inline-block will-change-transform">
                    {heroLines[2]}
                  </span>
                </span>
              </h1>
              <p className="hero-copy mt-6 max-w-xl text-xl leading-8 text-[#4f566b]">
                {t('hero.description')}
              </p>
              <div className="mt-8 grid max-w-[620px] gap-5 text-sm sm:grid-cols-2">
                <div className="min-h-[74px] border-l border-[#8b5cf6]/45 bg-[linear-gradient(90deg,rgba(139,92,246,0.12),rgba(255,255,255,0)_72%)] pl-4">
                  <p className="font-semibold text-[#24153f]">
                    {heroHighlights[0]}
                  </p>
                  <p className="mt-1 text-[#667085]">{heroHighlights[1]}</p>
                </div>
                <div className="min-h-[74px] border-l border-[#00a6d6]/45 bg-[linear-gradient(90deg,rgba(0,166,214,0.11),rgba(255,255,255,0)_72%)] pl-4">
                  <p className="font-semibold text-[#24153f]">
                    {heroHighlights[2]}
                  </p>
                  <p className="mt-1 text-[#667085]">{heroHighlights[3]}</p>
                </div>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <HeroButton>{actions[1]}</HeroButton>
                <HeroButton href="#showcase" variant="ghost">
                  {actions[2]}
                </HeroButton>
              </div>
            </div>

            <div className="relative min-h-[560px] lg:min-h-[620px]">
              <div
                aria-hidden="true"
                className="absolute -inset-8 rounded-[2.5rem] bg-[radial-gradient(circle_at_34%_32%,rgba(0,166,214,0.32),transparent_31%),radial-gradient(circle_at_78%_24%,rgba(139,92,246,0.24),transparent_28%),radial-gradient(circle_at_60%_84%,rgba(217,70,239,0.18),transparent_30%)] blur-md"
              />
              <div
                aria-hidden="true"
                className="absolute left-[7%] right-[7%] top-9 h-24 rounded-full border border-[#8b5cf6]/20 bg-white/30 shadow-[0_20px_80px_rgba(139,92,246,0.12)] [transform:skewY(-8deg)]"
              />
              <div
                aria-hidden="true"
                className="absolute bottom-10 left-[8%] right-[2%] h-px bg-[linear-gradient(90deg,transparent,#8b5cf6,#00a6d6,#d946ef,transparent)] shadow-[0_0_28px_rgba(0,166,214,0.42)]"
              />
              <div className="relative min-h-[560px] lg:min-h-[620px]">
                <VideoFrame
                  className="hero-card-motion hero-layer absolute left-0 top-16 min-h-[410px] w-[68%] rotate-[-3deg] shadow-[0_34px_90px_rgba(14,20,39,0.24)]"
                  clip={videoClips[9]}
                  priority
                />
                <VideoFrame
                  className="hero-card-motion hero-layer absolute right-0 top-0 min-h-[260px] w-[42%] rotate-[4deg] shadow-[0_28px_76px_rgba(0,80,115,0.22)]"
                  clip={videoClips[10]}
                />
                <VideoFrame
                  className="hero-card-motion hero-layer absolute bottom-12 right-8 min-h-[240px] w-[44%] rotate-[2deg] shadow-[0_26px_70px_rgba(49,32,79,0.2)]"
                  clip={videoClips[11]}
                />
                <div className="hero-ui-motion absolute left-[6%] top-3 rounded-lg border border-white/70 bg-white/78 px-4 py-3 shadow-[0_18px_46px_rgba(49,32,79,0.12)] backdrop-blur">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6d28d9]">
                    {heroPrompt[0]}
                  </p>
                  <p className="mt-1 max-w-[220px] text-sm font-semibold leading-5 text-[#24153f]">
                    {heroPrompt[1]}
                  </p>
                </div>
                <div className="hero-ui-motion absolute right-[6%] top-[43%] rounded-lg border border-[#c8e7f4] bg-[#eefaff]/86 px-4 py-3 shadow-[0_18px_46px_rgba(0,80,115,0.12)] backdrop-blur">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#006b8f]">
                    {heroPrompt[2]}
                  </p>
                  <div className="mt-2 grid gap-1.5">
                    <span className="h-1.5 w-32 bg-[#80d7f2]" />
                    <span className="h-1.5 w-24 bg-[#d8ccff]" />
                    <span className="h-1.5 w-28 bg-[#f2d6ff]" />
                  </div>
                </div>
                <div className="hero-ui-motion absolute bottom-0 left-[12%] right-[5%] overflow-hidden rounded-lg border border-white/70 bg-white/86 p-4 shadow-[0_24px_70px_rgba(0,80,115,0.2)] backdrop-blur">
                  <div className="flex items-center justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.16em] text-[#746987]">
                    <span>{heroPrompt[3]}</span>
                    <span className="text-[#006b8f]">{heroPrompt[4]}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-[1fr_auto] items-end gap-4">
                    <div className="space-y-2">
                      <div className="h-1.5 w-11/12 bg-[linear-gradient(90deg,#8b5cf6,#00a6d6,#d946ef)] shadow-[0_0_18px_rgba(0,166,214,0.24)]" />
                      <div className="h-1.5 w-7/12 bg-[#d8ccff]" />
                      <div className="h-1.5 w-9/12 bg-[#c8e7f4]" />
                    </div>
                    <span className="text-3xl font-semibold leading-none text-[#24153f]">
                      {heroPrompt[5]}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="home-section scroll-mt-24 border-y border-[#d8ccff] bg-white/58 px-4 py-16 sm:px-6 lg:px-10"
          id="overview"
        >
          <div className="mx-auto grid max-w-[1240px] gap-8 md:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="section-copy-motion font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6d28d9]">
                {overview[0]}
              </p>
              <h2 className="section-copy-motion mt-4 text-balance text-4xl font-semibold leading-tight text-[#141827]">
                {overview[1]}
              </h2>
            </div>
            <div>
              <p className="section-copy-motion max-w-2xl text-lg leading-8 text-[#596174]">
                {overview[2]}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {formats.map(format => (
                  <span
                    className="overview-chip-motion rounded-lg border border-[#d8ccff] bg-white/78 px-4 py-2 text-sm font-semibold text-[#31204f] shadow-[0_10px_26px_rgba(49,32,79,0.06)]"
                    key={format}
                  >
                    {format}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className="home-section relative isolate z-10 scroll-mt-24 overflow-visible border-y border-[#d8ccff] bg-[#eef7ff] text-[#141827]"
          id="showcase"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,#f7f9ff_0%,#eef7ff_18%,#f9fbff_58%,#eef7ff_100%),radial-gradient(circle_at_20%_24%,rgba(0,166,214,0.18),transparent_32%),radial-gradient(circle_at_78%_62%,rgba(139,92,246,0.16),transparent_34%)]"
          />
          <div className="mx-auto max-w-[1240px] px-4 pt-16 sm:px-6 lg:px-10">
            <div className="bento-heading relative z-10 flex items-end justify-between gap-6 border-b border-[#c8e7f4] pb-6">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#006b8f]">
                  {showcase[0]}
                </p>
                <h2 className="mt-4 max-w-3xl text-balance text-5xl font-semibold leading-tight text-[#141827]">
                  {showcase[1]}
                </h2>
                <p className="mt-4 max-w-2xl text-lg leading-8 text-[#596174]">
                  {showcase[2]}
                </p>
              </div>
              <span className="hidden font-mono text-sm text-[#667085] md:inline">
                {showcase[3]}
              </span>
            </div>
          </div>
          <FilmRail stories={showcaseStories} label={t('galleryLabel')} />
        </section>

        <section
          className="home-section relative z-0 scroll-mt-24 border-y border-[#d8ccff] bg-[#eef7ff] px-4 py-16 text-[#141827] sm:px-6 lg:px-10"
          id="materials"
        >
          <div className="mx-auto max-w-[1240px]">
            <div className="mb-8 flex items-end justify-between gap-6 border-b border-[#c8e7f4] pb-5">
              <div>
                <p className="section-copy-motion font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#006b8f]">
                  {materials[0]}
                </p>
                <h2 className="section-copy-motion mt-4 text-balance text-4xl font-semibold leading-tight text-[#141827]">
                  {materials[1]}
                </h2>
              </div>
              <span className="section-copy-motion hidden font-mono text-sm text-[#667085] md:inline">
                {materials[2]}
              </span>
            </div>
            <div className="material-flow-motion relative mb-8 overflow-hidden rounded-lg border border-white/70 bg-white/70 p-4 shadow-[0_24px_70px_rgba(0,80,115,0.11)] backdrop-blur will-change-transform">
              <div
                aria-hidden="true"
                className="material-line-motion absolute inset-x-8 top-1/2 h-px bg-[linear-gradient(90deg,#8b5cf6,#00a6d6,#d946ef)] will-change-transform"
              />
              <div className="relative grid gap-3 md:grid-cols-4">
                {pipelineSteps.map((step, index) => (
                  <div
                    className="material-step-motion rounded-lg border border-[#d8ccff] bg-[#f9fbff]/88 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] will-change-transform"
                    key={step.label}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#6d28d9]">
                        {step.label}
                      </span>
                      <span className="grid size-7 place-items-center rounded-full bg-[#e8f9ff] font-mono text-[10px] font-semibold text-[#006b8f]">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="mt-5 text-sm font-semibold text-[#163044]">
                      {step.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              {submitItems.map((item, index) => (
                <div
                  className="material-submit-motion rounded-lg border border-[#c8e7f4] bg-white/76 p-5 shadow-[0_18px_46px_rgba(0,80,115,0.08)] transition will-change-transform hover:border-[#80d7f2] hover:bg-white"
                  key={item}
                >
                  <div className="mb-6 flex items-center justify-between">
                    <span className="font-mono text-xs text-[#667085]">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="size-2 rounded-full bg-[#00a6d6] shadow-[0_0_18px_rgba(0,166,214,0.42)]" />
                  </div>
                  <p className="text-sm font-semibold leading-6 text-[#163044]">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="home-section scroll-mt-24 px-4 py-16 sm:px-6 lg:px-10"
          id="context"
        >
          <div className="mx-auto grid max-w-[1240px] gap-10 md:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="context-copy-motion font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6d28d9]">
                {context[0]}
              </p>
              <h2 className="context-copy-motion mt-4 text-balance text-4xl font-semibold leading-tight text-[#141827]">
                {context[1]}
              </h2>
              <p className="context-copy-motion mt-5 max-w-lg text-lg leading-8 text-[#596174]">
                {context[2]}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {platformGroups.map(group => (
                <div
                  className="context-card-motion rounded-lg border border-[#d8ccff] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(255,255,255,0.7))] p-5 shadow-[0_22px_60px_rgba(49,32,79,0.09),inset_0_1px_0_rgba(255,255,255,0.9)]"
                  key={group.title}
                >
                  <h3 className="font-semibold text-[#141827]">
                    {group.title}
                  </h3>
                  <ul className="mt-4 grid gap-3 text-sm text-[#596174]">
                    {group.items.map(item => (
                      <li className="border-t border-[#d8ccff] pt-3" key={item}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <p className="context-card-motion rounded-lg border border-[#f0c36c]/45 bg-[#fff8e8] p-4 text-sm leading-6 text-[#7c560f] sm:col-span-2">
                {context[3]}
              </p>
            </div>
          </div>
        </section>

        <section
          className="home-section scroll-mt-24 border-y border-[#d8ccff] bg-white/58 px-4 py-16 sm:px-6 lg:px-10"
          id="timeline"
        >
          <div className="mx-auto max-w-[1240px]">
            <p className="section-copy-motion font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6d28d9]">
              {timeline[0]}
            </p>
            <h2 className="section-copy-motion mt-4 text-balance text-4xl font-semibold leading-tight text-[#141827]">
              {timeline[1]}
            </h2>
            <ol className="mt-10 grid gap-4 md:grid-cols-3">
              {timeline.slice(2).map((item, index) => (
                <li
                  className="timeline-step-motion relative overflow-hidden rounded-lg border border-[#d8ccff] bg-white/78 p-5 shadow-[0_18px_46px_rgba(49,32,79,0.07)]"
                  key={item}
                >
                  <div
                    aria-hidden="true"
                    className="timeline-line-motion absolute inset-x-0 top-0 h-0.5 bg-[linear-gradient(90deg,#8b5cf6,#00a6d6)]"
                  />
                  <p className="font-mono text-xs text-[#6d28d9]">
                    {String(index + 1).padStart(2, '0')}
                  </p>
                  <p className="mt-8 text-lg font-semibold text-[#24153f]">
                    {item}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="footer-cta home-section relative isolate overflow-hidden border-t border-[#d8ccff] px-4 py-24 text-white sm:px-6 lg:px-10">
          <span
            aria-hidden="true"
            className="absolute inset-0 -z-30 bg-[linear-gradient(135deg,#24153f_0%,#07131b_52%,#006b8f_100%)]"
          />
          <LazyHomeVideo
            className="footer-video absolute inset-0 -z-20 size-full object-cover opacity-72 motion-reduce:hidden"
            decorative
            poster={videoClips[12].poster}
            src={videoClips[12].src}
          />
          <span
            aria-hidden="true"
            className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(7,19,27,0.9)_0%,rgba(36,21,63,0.7)_45%,rgba(0,166,214,0.35)_100%),linear-gradient(180deg,rgba(7,19,27,0.12)_0%,rgba(7,19,27,0.88)_100%)]"
          />
          <div
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,#8b5cf6,#00a6d6,#d946ef,transparent)]"
          />
          <div className="mx-auto grid max-w-[1240px] gap-8 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#a7ecff]">
                {footer[0]}
              </p>
              <h2 className="mt-4 max-w-3xl text-balance text-5xl font-semibold leading-tight text-white">
                {footer[1]}
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-white/78">
                {footer[2]}
              </p>
            </div>
            <div className="flex md:justify-end">
              <HeroButton>{actions[1]}</HeroButton>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#d8ccff] bg-[#eef7ff] px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-base font-semibold text-[#141827]">
              Mylo Studio
            </p>
            <p className="mt-1 text-sm text-[#667085]">
              {t('footerLegalNote')}
            </p>
          </div>
          <nav
            aria-label={t('legalNavLabel')}
            className="flex flex-wrap items-center gap-x-5 gap-y-2 md:justify-end"
          >
            <a
              className="text-sm text-[#006b8f] underline underline-offset-4 transition hover:text-[#004f6b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
              href="https://mylostudio.ai/ru/legal/offer"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('legalOffer')}
            </a>
            <a
              className="text-sm text-[#006b8f] underline underline-offset-4 transition hover:text-[#004f6b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
              href="https://mylostudio.ai/ru/legal/privacy"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('legalPrivacy')}
            </a>
            <a
              className="text-sm text-[#006b8f] underline underline-offset-4 transition hover:text-[#004f6b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
              href="https://mylostudio.ai/ru/legal/personal-data-consent"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('legalPersonalDataConsent')}
            </a>
            <a
              className="text-sm text-[#006b8f] underline underline-offset-4 transition hover:text-[#004f6b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8b5cf6]"
              href="mailto:support@mylostudio.ai"
            >
              support@mylostudio.ai
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};
